import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { adminService, ApplicantDetail, ApplicantListItem, ApplicantStatusCounts } from '@/services/admin.service'
import { interviewService } from '@/services/interview.service'
import { ApplicantStatus } from '@shared/types/entities'
import { ApiError } from '@/lib/api-client'
import { AdminLayout } from '@/components/AdminLayout'

const STATUS_KEYS: Record<string, string> = {
  REGISTERED: 'admin.statusRegistered',
  VIDEO_UPLOADED: 'admin.statusVideoUploaded',
  INTERVIEW_SCHEDULED: 'admin.statusInterviewScheduled',
  UNDER_REVIEW: 'admin.statusUnderReview',
  APPROVED: 'admin.statusApproved',
  REJECTED: 'admin.statusRejected',
  REVISION_REQUIRED: 'admin.statusRevisionRequired',
}

const TEMPLATE_KEYS = {
  revision: [
    { label: 'admin.tplRevisionVideoShort', text: 'admin.tplRevisionVideoShortText' },
    { label: 'admin.tplRevisionAudioBad', text: 'admin.tplRevisionAudioBadText' },
    { label: 'admin.tplRevisionPitchIncomplete', text: 'admin.tplRevisionPitchIncompleteText' },
    { label: 'admin.tplRevisionNotVisible', text: 'admin.tplRevisionNotVisibleText' },
    { label: 'admin.tplRevisionInterviewWeak', text: 'admin.tplRevisionInterviewWeakText' },
  ],
  approval: [
    { label: 'admin.tplApprovalStandard', text: 'admin.tplApprovalStandardText' },
    { label: 'admin.tplApprovalGood', text: 'admin.tplApprovalGoodText' },
  ],
  rejection: [
    { label: 'admin.tplRejectionNotSuitable', text: 'admin.tplRejectionNotSuitableText' },
    { label: 'admin.tplRejectionMultiple', text: 'admin.tplRejectionMultipleText' },
  ],
}

export function ApplicantReviewPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const [selectedStatus, setSelectedStatus] = useState<ApplicantStatus>(
    ApplicantStatus.VIDEO_UPLOADED
  )
  const [selectedApplicant, setSelectedApplicant] = useState<ApplicantListItem | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')

  const { data: countsData } = useQuery({
    queryKey: ['applicant-status-counts'],
    queryFn: () => adminService.getApplicantStatusCounts(token!),
    enabled: !!token,
    staleTime: 30000,
  })

  const counts: ApplicantStatusCounts | undefined = countsData?.counts

  const { data, isLoading } = useQuery({
    queryKey: ['applicants', selectedStatus],
    queryFn: () => adminService.listApplicants(selectedStatus, token!),
    enabled: !!token,
  })

  const { data: detail } = useQuery({
    queryKey: ['applicant-detail', selectedApplicant?.id],
    queryFn: () => adminService.getApplicant(selectedApplicant!.id, token!),
    enabled: !!selectedApplicant?.id && !!token,
  })

  const { data: interviewData } = useQuery({
    queryKey: ['interview-detail', selectedApplicant?.interviewId],
    queryFn: () => interviewService.getInterview(selectedApplicant!.interviewId!),
    enabled: !!selectedApplicant?.interviewId,
  })

  const reviewMutation = useMutation({
    mutationFn: ({
      applicantId,
      status,
      notes,
    }: {
      applicantId: string
      status: ApplicantStatus
      notes?: string
    }) =>
      adminService.reviewApplicant(
        applicantId,
        { status, reviewNotes: notes },
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicants'] })
      queryClient.invalidateQueries({ queryKey: ['applicant-status-counts'] })
      setSelectedApplicant(null)
      setReviewNotes('')
    },
  })

  const handleApprove = () => {
    if (selectedApplicant) {
      reviewMutation.mutate({
        applicantId: selectedApplicant.id,
        status: ApplicantStatus.APPROVED,
        notes: reviewNotes,
      })
    }
  }

  const handleReject = () => {
    if (selectedApplicant) {
      reviewMutation.mutate({
        applicantId: selectedApplicant.id,
        status: ApplicantStatus.REJECTED,
        notes: reviewNotes,
      })
    }
  }

  const handleRequestRevision = () => {
    if (selectedApplicant) {
      reviewMutation.mutate({
        applicantId: selectedApplicant.id,
        status: ApplicantStatus.REVISION_REQUIRED,
        notes: reviewNotes,
      })
    }
  }

  const STATUS_ORDER = [
    ApplicantStatus.VIDEO_UPLOADED,
    ApplicantStatus.INTERVIEW_SCHEDULED,
    ApplicantStatus.UNDER_REVIEW,
    ApplicantStatus.REVISION_REQUIRED,
    ApplicantStatus.REGISTERED,
    ApplicantStatus.APPROVED,
    ApplicantStatus.REJECTED,
  ]

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.applicantReview')}</h1>
          <p className="text-gray-600">{t('admin.applicantReviewDesc')}</p>
        </div>

        {/* Status Tabs with Badges */}
        <div className="bg-white rounded-lg shadow p-2">
          <div className="flex flex-wrap gap-2">
            {STATUS_ORDER.map((status) => {
              const count = counts?.[status] ?? 0
              const isActive = selectedStatus === status
              const needsAttention = [
                ApplicantStatus.VIDEO_UPLOADED,
                ApplicantStatus.INTERVIEW_SCHEDULED,
                ApplicantStatus.UNDER_REVIEW,
                ApplicantStatus.REVISION_REQUIRED,
              ].includes(status)

              return (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`
                    relative px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status}
                  {count > 0 && (
                    <span
                      className={`
                        ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold
                        ${isActive
                          ? 'bg-white text-blue-600'
                          : needsAttention
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-300 text-gray-700'
                        }
                      `}
                    >
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bewerberliste */}
          <div className="bg-white rounded-lg shadow flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('admin.applicantList')}</h2>
            </div>

            <div className="divide-y divide-gray-200 flex-1 overflow-y-auto max-h-[600px]">
              {isLoading ? (
                <div className="p-4 text-center text-gray-600">{t('common.loading')}</div>
              ) : data?.applicants && data.applicants.length > 0 ? (
                data.applicants.map((applicant) => (
                  <button
                    key={applicant.id}
                    onClick={() => setSelectedApplicant(applicant)}
                    className={`w-full p-4 text-left hover:bg-gray-50 ${
                      selectedApplicant?.id === applicant.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">
                      {applicant.firstName} {applicant.lastName}
                    </div>
                    <div className="text-sm text-gray-600">{applicant.email}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('admin.registered')} {new Date(applicant.createdAt).toLocaleDateString('de-DE')}
                    </div>
                    {applicant.videoUploadedAt && (
                      <div className="text-xs text-green-600 mt-1">
                        {t('admin.videoUploaded')}
                      </div>
                    )}
                    {applicant.interviewScheduledAt && (
                      <div className="text-xs text-indigo-600 mt-1">
                        Interview: {new Date(applicant.interviewScheduledAt).toLocaleString('de-DE', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600">
                  {t('admin.noApplicantsWithStatus', { status: STATUS_KEYS[selectedStatus] ? t(STATUS_KEYS[selectedStatus]) : selectedStatus })}
                </div>
              )}
            </div>
          </div>

          {/* Bewerber-Details & Bewertung */}
          <div className="bg-white rounded-lg shadow flex flex-col max-h-[calc(100vh-200px)]">
            {selectedApplicant ? (
              <>
              <div className="p-6 flex-1 overflow-y-auto">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('admin.applicantDetails')}
                </h2>

                <div className="space-y-3 mb-6">
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('admin.name')}:</span>
                    <p className="text-gray-900">
                      {selectedApplicant.firstName} {selectedApplicant.lastName}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('admin.email')}:</span>
                    <p className="text-gray-900">{selectedApplicant.email}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('admin.phone')}:</span>
                    <p className="text-gray-900">{selectedApplicant.phone}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('admin.status')}:</span>
                    <p className="text-gray-900">{STATUS_KEYS[selectedApplicant.status] ? t(STATUS_KEYS[selectedApplicant.status]) : selectedApplicant.status}</p>
                  </div>
                  {selectedApplicant.scriptVersion && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('admin.script')}:</span>
                      <p className="text-gray-900">
                        {selectedApplicant.scriptVersion} ({t('admin.downloadedOn')}{' '}
                        {new Date(selectedApplicant.scriptDownloadedAt).toLocaleDateString('de-DE')})
                      </p>
                    </div>
                  )}
                </div>

                {/* Interview-Info */}
                {selectedApplicant.interviewScheduledAt && (
                  <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-md">
                    <h3 className="text-sm font-medium text-indigo-900 mb-2">Interview</h3>
                    <p className="text-sm text-indigo-700">
                      Termin: {new Date(selectedApplicant.interviewScheduledAt).toLocaleString('de-DE', {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })} Uhr
                    </p>
                    {selectedApplicant.status === 'INTERVIEW_SCHEDULED' && (
                      <p className="text-xs text-indigo-600 mt-2">
                        Bewertung über die Interview-Verwaltung durchführen.
                      </p>
                    )}
                  </div>
                )}

                {/* Interview-Bewertung */}
                {interviewData?.interview && interviewData.interview.checklist?.length > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Interview-Bewertung</h3>
                    {interviewData.interview.reviewedAt && (
                      <p className="text-xs text-gray-500 mb-3">
                        Bewertet am {new Date(interviewData.interview.reviewedAt).toLocaleString('de-DE', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>
                    )}
                    <div className="space-y-2 mb-3">
                      {interviewData.interview.checklist.map(item => (
                        <div key={item.id} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-gray-100">
                          <span className="text-gray-700">{item.label}</span>
                          <span className={`font-medium ${
                            item.rating === 'BESTANDEN' ? 'text-green-600' :
                            item.rating === 'TEILWEISE' ? 'text-yellow-600' :
                            item.rating === 'NICHT_BESTANDEN' ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {item.rating === 'BESTANDEN' ? '✓ Bestanden' :
                             item.rating === 'TEILWEISE' ? '~ Teilweise' :
                             item.rating === 'NICHT_BESTANDEN' ? '✗ Nicht bestanden' : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                    {interviewData.interview.reviewNotes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-xs font-medium text-gray-600">Notizen:</span>
                        <p className="text-sm text-gray-700 mt-1">{interviewData.interview.reviewNotes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Video-Player */}
                {(detail?.videoPlayUrl || selectedApplicant.videoUrl) && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      {t('admin.qualificationVideo')}
                    </h3>
                    {detail?.videoPlayUrl ? (
                      <video
                        src={detail.videoPlayUrl}
                        controls
                        className="w-full rounded-md bg-black"
                      />
                    ) : (
                      <div className="bg-gray-100 rounded-md p-4 text-center">
                        <p className="text-sm text-gray-600">{t('common.loading')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Bewertungsnotizen */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.reviewNotes')}
                  </label>

                  {/* Textbausteine */}
                  <div className="mb-2 space-y-2">
                    <div>
                      <span className="text-xs font-medium text-yellow-700">{t('admin.revisionTemplates')}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {TEMPLATE_KEYS.revision.map((tpl) => (
                          <button
                            key={tpl.label}
                            type="button"
                            onClick={() => setReviewNotes(t(tpl.text))}
                            className="px-2 py-0.5 text-xs bg-yellow-50 border border-yellow-200 rounded hover:bg-yellow-100"
                          >
                            {t(tpl.label)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-green-700">{t('admin.approvalTemplates')}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {TEMPLATE_KEYS.approval.map((tpl) => (
                          <button
                            key={tpl.label}
                            type="button"
                            onClick={() => setReviewNotes(t(tpl.text))}
                            className="px-2 py-0.5 text-xs bg-green-50 border border-green-200 rounded hover:bg-green-100"
                          >
                            {t(tpl.label)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-red-700">{t('admin.rejectionTemplates')}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {TEMPLATE_KEYS.rejection.map((tpl) => (
                          <button
                            key={tpl.label}
                            type="button"
                            onClick={() => setReviewNotes(t(tpl.text))}
                            className="px-2 py-0.5 text-xs bg-red-50 border border-red-200 rounded hover:bg-red-100"
                          >
                            {t(tpl.label)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('admin.reviewNotesPlaceholder')}
                  />
                </div>

                {reviewMutation.isError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">
                      {reviewMutation.error instanceof ApiError
                        ? reviewMutation.error.message
                        : t('admin.reviewFailed')}
                    </p>
                  </div>
                )}

                {reviewMutation.isSuccess && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600">{t('admin.reviewSuccess')}</p>
                  </div>
                )}
              </div>

              {/* Sticky Action Bar */}
              <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg flex space-x-3">
                <button
                  onClick={handleApprove}
                  disabled={reviewMutation.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  {reviewMutation.isPending ? t('admin.processing') : t('admin.approve')}
                </button>
                <button
                  onClick={handleRequestRevision}
                  disabled={reviewMutation.isPending}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 text-sm font-medium"
                >
                  {t('admin.requestRevision')}
                </button>
                <button
                  onClick={handleReject}
                  disabled={reviewMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                >
                  {t('admin.reject')}
                </button>
              </div>
              </>
            ) : (
              <div className="p-6 text-center text-gray-600">
                {t('admin.selectApplicantToReview')}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
